import { useMemo, useState } from "react";
import { Show } from "shared/components";
import { Card } from "shared/components/Card";
import { Loader } from "shared/components/Loader";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils";
import { useMedia } from "shared/providers/media-query/use-media";
import { AnalyticsEmptyState } from "./AnalyticsEmptyState";
import { SingleArrow, BranchConnector } from "./PipelineArrows";
import { getNiceAxis, getTicks, splitStageTransitions } from "../utils";

const DEFAULT_ARROW_COLOR = "#6E7079";
const FALLBACK_SOURCE_NAME = "Unknown";
const CHART_HEIGHT = 240;

export type PipelineTransition = {
  stage_id: string | null;
  stage_name: string;
  count: number;
};

export type PipelineSourceBreakdown = {
  source_id: number | null;
  source_name: string | null;
  count: number;
  percentage: number;
};

export type PipelineStage = {
  stage_id: string;
  stage_name: string;
  total_count: number;
  transitions: PipelineTransition[];
  by_source?: PipelineSourceBreakdown[];
};

type TransitionItem = {
  key: string;
  count: number;
  label: string;
  pct: number;
  color: string;
};

const buildTransitions = (
  stage: PipelineStage,
  getTransitionColor: ((transition: PipelineTransition) => string) | undefined,
  getTransitionLabel: ((transition: PipelineTransition) => string) | undefined,
  stageLabel: string
): TransitionItem[] => {
  const { stillInStage, movements, base } = splitStageTransitions(stage);
  const resolveLabel = (t: PipelineTransition) =>
    getTransitionLabel?.(t) ?? t.stage_name;
  const items: TransitionItem[] = movements.map((m) => ({
    key: m.stage_id ?? `other-${m.stage_name}`,
    count: m.count,
    label: `Moved into ${resolveLabel(m)}`,
    pct: Math.round((m.count / base) * 100),
    color: getTransitionColor?.(m) ?? DEFAULT_ARROW_COLOR
  }));
  if (stillInStage && stillInStage.count > 0)
    items.push({
      key: "still",
      count: stillInStage.count,
      label: `Still in ${stageLabel}`,
      pct: Math.round((stillInStage.count / base) * 100),
      color: getTransitionColor?.(stillInStage) ?? DEFAULT_ARROW_COLOR
    });
  return items;
};

const TooltipBody = ({
  stage,
  stageLabel,
  variant,
  getSourceColor,
  getTransitionColor,
  getTransitionLabel
}: {
  stage: PipelineStage;
  stageLabel: string;
  variant: "stacked" | "single";
  getSourceColor: ((sourceId: number | null) => string) | undefined;
  getTransitionColor: ((transition: PipelineTransition) => string) | undefined;
  getTransitionLabel: ((transition: PipelineTransition) => string) | undefined;
}) => {
  const items = buildTransitions(
    stage,
    getTransitionColor,
    getTransitionLabel,
    stageLabel
  );
  const isSingle = items.length === 1;
  const bySource = stage.by_source ?? [];
  const showBySource = variant === "stacked" && bySource.length > 0;
  const colors = items.map((i) => i.color);

  return (
    <>
      <div
        className={cx(
          "flex justify-center",
          isSingle ? "items-center" : "items-start"
        )}
      >
        <div
          className={cx(
            "shrink-0 flex items-center gap-2 mr-2",
            !isSingle && "pt-1.5"
          )}
        >
          <span className="text-xs font-medium text-content-1000 whitespace-nowrap">
            {stageLabel}
          </span>
          <span className="text-xs font-bold text-content-1000">
            {stage.total_count}
          </span>
        </div>

        {isSingle ? (
          <SingleArrow color={items[0]?.color} />
        ) : (
          <BranchConnector count={items.length} colors={colors} />
        )}

        <div className="flex-1 ml-2">
          {items.map((t) => (
            <div
              key={t.key}
              className="grid grid-cols-[32px_1fr_48px] items-center gap-2 h-7"
            >
              <span className="text-right text-xs font-bold text-content-1000">
                {t.count}
              </span>
              <span className="text-xs font-medium text-content-1000 truncate">
                {t.label}
              </span>
              <span className="text-center text-xs font-bold text-content-700 bg-alpha-40 px-2 py-0.5 rounded-full">
                {t.pct}%
              </span>
            </div>
          ))}
        </div>
      </div>

      <Show if={showBySource}>
        <div className="border-t border-alpha-80 my-3" />
        <p className="text-xs font-bold text-content-300 uppercase mb-1">
          By source
        </p>
        <div className="space-y-2">
          {bySource.map((src) => (
            <div
              key={src.source_id ?? "unknown"}
              className="grid grid-cols-[16px_1fr_32px_48px] items-center gap-2"
            >
              <span
                className="w-4 h-4 rounded-full border border-alpha-100"
                style={{
                  backgroundColor:
                    getSourceColor?.(src.source_id) ?? DEFAULT_ARROW_COLOR
                }}
              />
              <span className="text-xs font-medium text-content-1000 truncate">
                {src.source_name ?? FALLBACK_SOURCE_NAME}
              </span>
              <span className="text-right text-xs font-medium text-content-1000">
                {src.count}
              </span>
              <span className="text-center text-xs font-bold text-content-700 bg-alpha-40 px-2 py-0.5 rounded-full">
                {Math.round(src.percentage)}%
              </span>
            </div>
          ))}
        </div>
      </Show>
    </>
  );
};

type StageColumnProps = {
  stage: PipelineStage;
  stageLabel: string;
  axisMax: number;
  variant: "stacked" | "single";
  stageColor: string;
  getSourceColor: ((sourceId: number | null) => string) | undefined;
  getTransitionColor: ((transition: PipelineTransition) => string) | undefined;
  getTransitionLabel: ((transition: PipelineTransition) => string) | undefined;
  isHovered: boolean;
  isNearEnd: boolean;
  isMobile: boolean;
  onHoverChange: (stageId: string | null) => void;
  onClick: ((stage: PipelineStage) => void) | undefined;
};

const StageColumn = ({
  stage,
  stageLabel,
  axisMax,
  variant,
  stageColor,
  getSourceColor,
  getTransitionColor,
  getTransitionLabel,
  isHovered,
  isNearEnd,
  isMobile,
  onHoverChange,
  onClick
}: StageColumnProps) => {
  const hasTransitions = stage.transitions.some((t) => t.count > 0);
  const tooltipOpen =
    !isMobile && isHovered && stage.total_count > 0 && hasTransitions;
  const clickable = stage.total_count > 0 && Boolean(onClick);
  const singleBarHeight =
    axisMax > 0 ? `${(stage.total_count / axisMax) * CHART_HEIGHT}px` : "0px";
  const bySource = stage.by_source ?? [];

  const handleActivate = () => {
    if (clickable) onClick?.(stage);
  };

  const getTriggerOffsetClass = () => {
    if (isNearEnd && variant === "single") return "translate-x-[65px]";
    if (isNearEnd) return "translate-x-[108px]";
    if (variant === "single") return "-translate-x-[50px]";
    return "-translate-x-[65px]";
  };

  return (
    <div
      role="button"
      aria-label={`${stageLabel}: ${stage.total_count}`}
      aria-disabled={!clickable}
      tabIndex={clickable ? 0 : -1}
      className={cx(
        "flex-1 flex flex-col items-center relative",
        clickable && "cursor-pointer"
      )}
      onMouseEnter={() => onHoverChange(stage.stage_id)}
      onMouseLeave={() => onHoverChange(null)}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate();
        }
      }}
    >
      <span className="text-xs font-bold text-content-500 mb-1">
        {stage.total_count}
      </span>

      <div className="w-full max-w-[100px] flex flex-col-reverse rounded overflow-hidden">
        {variant === "stacked" ? (
          bySource.map((src) => (
            <div
              key={src.source_id ?? "unknown"}
              className="border-t border-alpha-100 first:border-t-0"
              style={{
                height:
                  axisMax > 0
                    ? `${(src.count / axisMax) * CHART_HEIGHT}px`
                    : "0px",
                backgroundColor:
                  getSourceColor?.(src.source_id) ?? DEFAULT_ARROW_COLOR
              }}
            />
          ))
        ) : (
          <div
            style={{ height: singleBarHeight, backgroundColor: stageColor }}
          />
        )}
      </div>

      <Tooltip
        placement={isNearEnd ? "left-end" : "right-end"}
        open={tooltipOpen}
        onOpenChange={(open) => {
          if (!open) onHoverChange(null);
        }}
      >
        <TooltipTrigger asChild>
          <span
            aria-hidden="true"
            className={cx(
              "absolute top-0 left-1/3 right-1/2 pointer-events-none translate-y-4",
              getTriggerOffsetClass()
            )}
          />
        </TooltipTrigger>
        <TooltipContent
          className={cx(
            "rounded-lg border border-alpha-100 bg-content-1000-inverted shadow-xl p-4",
            isMobile ? "max-w-[360px]" : "min-w-[460px]"
          )}
        >
          <TooltipBody
            stage={stage}
            stageLabel={stageLabel}
            variant={variant}
            getSourceColor={getSourceColor}
            getTransitionColor={getTransitionColor}
            getTransitionLabel={getTransitionLabel}
          />
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

export const PipelineCard = <S extends PipelineStage>({
  data,
  isLoading,
  title,
  subtitle,
  helpText,
  variant,
  getStageColor,
  getStageLabel,
  getSourceColor,
  getTransitionColor,
  getTransitionLabel,
  onStageClick
}: {
  data: S[] | undefined;
  isLoading: boolean;
  title: string;
  subtitle: string;
  helpText: string;
  variant: "stacked" | "single";
  getStageColor: (stage: S) => string;
  getStageLabel?: (stage: S) => string;
  getSourceColor?: (sourceId: number | null) => string;
  getTransitionColor?: (transition: PipelineTransition) => string;
  getTransitionLabel?: (transition: PipelineTransition) => string;
  onStageClick?: (stage: S) => void;
}) => {
  const { isMobile } = useMedia();
  const visibleData = data ?? [];
  const hasData = visibleData.some((s) => s.total_count > 0);
  const showEmptyState = !isLoading && !hasData;
  const showChart = !isLoading && hasData;
  const [hoveredStageId, setHoveredStageId] = useState<string | null>(null);

  const maxCount = useMemo(
    () => Math.max(0, ...visibleData.map((s) => s.total_count)),
    [visibleData]
  );

  const { axisMax, tickCount } = hasData
    ? getNiceAxis(maxCount)
    : { axisMax: 0, tickCount: 0 };
  const ticks = hasData ? getTicks(axisMax, tickCount) : [];

  const columnOnClick = onStageClick as
    | ((stage: PipelineStage) => void)
    | undefined;

  return (
    <Card className="p-6 w-full">
      <div className="space-y-1">
        <div className="flex items-center gap-1.5">
          <p className="text-base font-medium text-content-700">{title}</p>
          <Tooltip placement="right-start">
            <TooltipTrigger asChild>
              <span className="text-content-300 cursor-pointer">
                <HugeiconsIcon icon={InformationCircleIcon} size={16} />
              </span>
            </TooltipTrigger>
            <TooltipContent
              className={cx(
                "w-full p-4 rounded-lg bg-background-1000",
                isMobile ? "max-w-[400px]" : "max-w-[659px]"
              )}
            >
              <p className="text-sm font-medium text-content-1000">
                {helpText}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
        <p className="text-sm font-medium text-content-300">{subtitle}</p>
      </div>

      <Show if={isLoading}>
        <div className="flex items-center justify-center min-h-[200px]">
          <Loader size={24} />
        </div>
      </Show>

      <Show if={showEmptyState}>
        <AnalyticsEmptyState description="There are no data to show for the selected filter" />
      </Show>

      <Show if={showChart}>
        <div className="mt-6 relative">
          <div className="overflow-x-auto">
            <div className={cx("flex pt-6", isMobile ? "min-w-[500px]" : "")}>
              <div
                className="shrink-0 w-10 mr-2 relative"
                style={{ height: CHART_HEIGHT }}
              >
                {ticks.map((tick) => (
                  <span
                    key={tick}
                    className="absolute right-0 text-xs font-medium text-content-300 leading-none"
                    style={{
                      bottom: `${(tick / axisMax) * 100}%`,
                      transform: "translateY(50%)"
                    }}
                  >
                    {tick}
                  </span>
                ))}
              </div>

              <div className="flex-1 relative">
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ height: CHART_HEIGHT }}
                >
                  {ticks.map((tick) => (
                    <div
                      key={tick}
                      className="absolute w-full border-t border-dashed border-alpha-100"
                      style={{ bottom: `${(tick / axisMax) * 100}%` }}
                    />
                  ))}
                </div>

                <div
                  className="flex items-end gap-2"
                  style={{ height: CHART_HEIGHT }}
                >
                  {visibleData.map((stage, idx) => (
                    <StageColumn
                      key={stage.stage_id}
                      stage={stage}
                      stageLabel={getStageLabel?.(stage) ?? stage.stage_name}
                      axisMax={axisMax}
                      variant={variant}
                      stageColor={getStageColor(stage)}
                      getSourceColor={getSourceColor}
                      getTransitionColor={getTransitionColor}
                      getTransitionLabel={getTransitionLabel}
                      isHovered={hoveredStageId === stage.stage_id}
                      isNearEnd={
                        variant === "single"
                          ? idx >= visibleData.length - 4
                          : idx >= visibleData.length - 3
                      }
                      isMobile={isMobile}
                      onHoverChange={setHoveredStageId}
                      onClick={columnOnClick}
                    />
                  ))}
                </div>

                <div className="flex gap-2 mt-4">
                  {visibleData.map((stage) => (
                    <div
                      key={stage.stage_id}
                      className="flex-1 min-w-0 px-1.5 flex justify-center cursor-pointer"
                      onClick={() => {
                        if (stage.total_count > 0) onStageClick?.(stage);
                      }}
                    >
                      <span className="text-xs font-medium text-content-500 text-center">
                        {getStageLabel?.(stage) ?? stage.stage_name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Show>
    </Card>
  );
};
